import type { TFunction } from 'i18next';
import { toJS } from 'mobx';

import type { Campaign, DisplayCreative, FacebookDisplayCreative } from '@feathr/blackbox';
import { AdTagCreative, CampaignState, CreativeClass } from '@feathr/blackbox';
import {
  extractMetaFeedback,
  getDisplayCreativeStatus,
  getMetaAdStatusLabel,
  mapMetaAdStatusToVisual,
} from '@feathr/extender/components/Creatives/Creatives.utils';

import {
  hasValidAspectRatio,
  validateAdTagCreative,
} from '../AdWizardCreativesStep/AdWizardCreativesStep.utils';

export type TCreativeStatus = 'ready' | 'warning' | 'error';

export interface ICreativeStatusInfo {
  /** Undefined when no status data exists yet (hides the status icon) */
  status?: TCreativeStatus;
  errors: string[];
}

/**
 * Derives the visual status and collects all error/warning messages for a creative
 * in a single pass. Validation errors always produce 'error'; if none, ad_status
 * determines the result.
 *
 * @param creative - The creative to evaluate
 * @param campaign - The campaign the creative belongs to
 * @param t - Translation function
 * @returns Status and array of error/warning messages
 */
export function getCreativeStatusInfo(
  creative: DisplayCreative,
  campaign: Campaign,
  t: TFunction,
): ICreativeStatusInfo {
  const validationErrors = getValidationErrors(creative, campaign);
  const errors = [...validationErrors];
  const hasValidationErrors = validationErrors.length > 0;

  if (creative.isFacebook) {
    const fbCreative = creative as FacebookDisplayCreative;
    const adStatus = fbCreative.get('ad_status');

    // Add ad_status errors if present
    if (adStatus && adStatus !== 'ACTIVE') {
      const label = getMetaAdStatusLabel(adStatus, t);
      const feedback = extractMetaFeedback(fbCreative.get('ad_review_feedback'));
      errors.push(feedback ? `${label}: ${feedback}` : label);
    }

    // Determine status: validation errors take priority, then ad_status
    if (hasValidationErrors) {
      return { status: 'error', errors };
    }
    if (!adStatus) {
      // If published, show pending; otherwise hide status
      if (campaign.get('state') === CampaignState.Published) {
        errors.push(t('Pending review'));
        return { status: 'warning', errors };
      }
      return { status: undefined, errors };
    }
    return { status: mapMetaAdStatusToVisual(adStatus), errors };
  }

  // Display creative
  const auditStatuses = creative.get('audit_statuses') ?? [];

  // Add audit status errors if present
  if (auditStatuses.length > 0) {
    const displayStatus = getDisplayCreativeStatus(creative, t);
    if (displayStatus.tooltip && displayStatus.status !== 'ready') {
      // Split by newlines so each auditor entry renders on its own line
      errors.push(...displayStatus.tooltip.split('\n'));
    }

    // Determine status: validation errors take priority, then audit status
    if (hasValidationErrors) {
      return { status: 'error', errors };
    }
    return { status: displayStatus.status, errors };
  }

  // No audit_statuses yet
  if (hasValidationErrors) {
    return { status: 'error', errors };
  }
  // If published, show pending; otherwise hide status
  if (campaign.get('state') === CampaignState.Published) {
    errors.push(t('Pending review'));
    return { status: 'warning', errors };
  }
  return { status: undefined, errors };
}

/**
 * Collects field-level validation errors for a single creative.
 */
function getValidationErrors(creative: DisplayCreative, campaign: Campaign): string[] {
  let errors: string[] = toJS(
    creative.validate(['width', 'height', 'spec', 'destination_url', 'name'], false).errors,
  );

  const validDimensions = creative.getValidDimensions(campaign);
  const specs = Object.values(validDimensions).map((v) => v.spec);
  const creativeIsValidDimensions = specs.includes(creative.get('spec'));
  const creativeHasValidAspectRatio = hasValidAspectRatio(creative, validDimensions);

  if (!creativeIsValidDimensions && !creativeHasValidAspectRatio) {
    errors.push(
      `${creative.get('width')}x${creative.get('height')} does not match any standard ad size. Images must have exact aspect ratios and meet minimum dimensions.`,
    );
  }

  if (
    creative instanceof AdTagCreative &&
    (creative as AdTagCreative).get('_cls') === CreativeClass.DisplayAdTag
  ) {
    errors = errors.concat(validateAdTagCreative(creative as AdTagCreative));
  }

  return errors;
}
